import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../utils/api";

export const fetchQuotes = createAsyncThunk("quotes/fetchAll", async (params = {}) => {
  const response = await api.get("/quotes", { params });
  return response.data;
});

export const fetchQuote = createAsyncThunk("quotes/fetchOne", async (id) => {
  const response = await api.get(`/quotes/${id}`);
  return response.data;
});

export const createQuote = createAsyncThunk("quotes/create", async (quoteData) => {
  const response = await api.post("/quotes", quoteData);
  return response.data;
});

export const updateQuote = createAsyncThunk("quotes/update", async ({ id, updates }) => {
  const response = await api.patch(`/quotes/${id}`, updates);
  return response.data;
});

export const deleteQuote = createAsyncThunk("quotes/delete", async (id) => {
  await api.delete(`/quotes/${id}`);
  return id;
});

export const sendQuote = createAsyncThunk("quotes/send", async (id) => {
  const response = await api.post(`/quotes/${id}/send`);
  return { id, ...response.data };
});

export const duplicateQuote = createAsyncThunk("quotes/duplicate", async (id) => {
  const response = await api.post(`/quotes/${id}/duplicate`);
  return response.data;
});

const quotesSlice = createSlice({
  name: "quotes",
  initialState: {
    list: [],
    currentQuote: null,
    totalQuotes: 0,
    totalPages: 0,
    currentPage: 1,
    status: "idle",
    createStatus: "idle",
    sendStatus: "idle",
    error: null,
  },
  reducers: {
    clearCurrentQuote(state) {
      state.currentQuote = null;
    },
    clearCreateStatus(state) {
      state.createStatus = "idle";
    },
    clearSendStatus(state) {
      state.sendStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchQuotes.pending, (state) => { state.status = "loading"; })
      .addCase(fetchQuotes.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.list = action.payload.quotes;
        state.totalQuotes = action.payload.totalQuotes;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchQuotes.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(fetchQuote.fulfilled, (state, action) => {
        state.currentQuote = action.payload;
      })
      .addCase(createQuote.pending, (state) => { state.createStatus = "loading"; })
      .addCase(createQuote.fulfilled, (state, action) => {
        state.createStatus = "succeeded";
        state.list.unshift(action.payload);
      })
      .addCase(createQuote.rejected, (state, action) => {
        state.createStatus = "failed";
        state.error = action.error.message;
      })
      .addCase(updateQuote.fulfilled, (state, action) => {
        const idx = state.list.findIndex((q) => q._id === action.payload._id);
        if (idx !== -1) state.list[idx] = action.payload;
        if (state.currentQuote?._id === action.payload._id) {
          state.currentQuote = action.payload;
        }
      })
      .addCase(deleteQuote.fulfilled, (state, action) => {
        state.list = state.list.filter((q) => q._id !== action.payload);
      })
      .addCase(sendQuote.pending, (state) => { state.sendStatus = "loading"; })
      .addCase(sendQuote.fulfilled, (state, action) => {
        state.sendStatus = "succeeded";
        const idx = state.list.findIndex((q) => q._id === action.payload.id);
        if (idx !== -1) state.list[idx].status = "sent";
      })
      .addCase(sendQuote.rejected, (state) => { state.sendStatus = "failed"; })
      .addCase(duplicateQuote.fulfilled, (state, action) => {
        state.list.unshift(action.payload);
      });
  },
});

export const { clearCurrentQuote, clearCreateStatus, clearSendStatus } = quotesSlice.actions;
export default quotesSlice.reducer;
